// This provider is only used in client-side context

import { createContext, useContext, useEffect, useRef, useCallback, ReactNode } from 'react';
import { useFeatures } from './FeatureProvider';

interface FeatureGatingContextType {
  // No additional methods exposed - this provider handles DOM manipulation internally
}

const FeatureGatingContext = createContext<FeatureGatingContextType | null>(null);

interface FeatureGatingProviderProps {
  children: ReactNode;
  fallbackBehavior?: 'hide' | 'disable' | 'blur' | 'upgrade';
  onFeatureBlock?: (featureName: string) => void;
  onUpgradeClick?: (featureName: string) => void;
}

export function FeatureGatingProvider({
  children,
  fallbackBehavior = 'hide',
  onFeatureBlock,
  onUpgradeClick
}: FeatureGatingProviderProps) {
  const { features, loading } = useFeatures();

  // DOM processing refs
  const observerRef = useRef<MutationObserver | null>(null);
  const processedElements = useRef<WeakSet<Element>>(new WeakSet());

  const applyFallback = useCallback((element: Element, strategy: string, featureName: string) => {
    const htmlElement = element as HTMLElement;

    // Store original state
    if (!htmlElement.dataset.useCeloOriginalDisplay) {
      htmlElement.dataset.useCeloOriginalDisplay = window.getComputedStyle(htmlElement).display;
    }

    switch (strategy || fallbackBehavior) {
      case 'hide':
        htmlElement.style.display = 'none';
        break;

      case 'disable':
        htmlElement.style.opacity = '0.5';
        htmlElement.style.pointerEvents = 'none';
        htmlElement.setAttribute('aria-disabled', 'true');
        break;

      case 'blur':
        htmlElement.style.filter = 'blur(4px)';
        htmlElement.style.position = 'relative';

        const overlay = document.createElement('div');
        overlay.className = 'usecelo-blur-overlay';
        overlay.style.cssText = `
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0,0,0,0.1);
          backdrop-filter: blur(2px);
          z-index: 10;
          cursor: pointer;
        `;
        overlay.innerHTML = `
          <div style="background: white; padding: 20px; border-radius: 8px; text-align: center; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
            <p style="margin: 0 0 10px 0; color: #333;">This feature requires an upgrade</p>
            <button style="background: #10b981; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">
              Upgrade Plan
            </button>
          </div>
        `;
        overlay.addEventListener('click', () => {
          onUpgradeClick?.(featureName);
        });
        htmlElement.appendChild(overlay);
        break;

      case 'upgrade':
        if (!htmlElement.dataset.useCeloOriginalContent) {
          htmlElement.dataset.useCeloOriginalContent = htmlElement.innerHTML;
        }
        htmlElement.innerHTML = `
          <div class="usecelo-upgrade-prompt" style="padding: 20px; background: #f3f4f6; border-radius: 8px; text-align: center;">
            <p style="margin-bottom: 10px; color: #333;">
              ${featureName.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} requires an upgrade
            </p>
            <button onclick="window.useCelo?.showUpgrade?.('${featureName}')" style="background: #10b981; color: white; padding: 8px 16px; border-radius: 4px; border: none; cursor: pointer;">
              Upgrade Plan
            </button>
          </div>
        `;
        break;

      default:
        htmlElement.style.display = 'none';
    }

    onFeatureBlock?.(featureName);
  }, [fallbackBehavior, onFeatureBlock, onUpgradeClick]);

  const restoreElement = useCallback((element: Element) => {
    const htmlElement = element as HTMLElement;

    // Restore display
    if (htmlElement.dataset.useCeloOriginalDisplay) {
      htmlElement.style.display = htmlElement.dataset.useCeloOriginalDisplay;
      delete htmlElement.dataset.useCeloOriginalDisplay;
    }

    // Restore content
    if (htmlElement.dataset.useCeloOriginalContent) {
      htmlElement.innerHTML = htmlElement.dataset.useCeloOriginalContent;
      delete htmlElement.dataset.useCeloOriginalContent;
    }

    // Remove overlays
    const overlay = htmlElement.querySelector('.usecelo-blur-overlay');
    if (overlay) overlay.remove();

    // Restore styles
    htmlElement.style.opacity = '';
    htmlElement.style.pointerEvents = '';
    htmlElement.style.filter = '';
    htmlElement.removeAttribute('aria-disabled');
  }, []);

  const processElement = useCallback((element: Element) => {
    if (processedElements.current.has(element)) return;

    const featureName = element.getAttribute('data-feature');
    if (!featureName) return;

    const featureAccess = features.find(f => f.tag === featureName);
    const fallbackStrategy = element.getAttribute('data-fallback');

    if (!featureAccess?.hasAccess) {
      applyFallback(element, fallbackStrategy || fallbackBehavior, featureName);
    } else {
      restoreElement(element);
    }

    processedElements.current.add(element);
  }, [features, fallbackBehavior, applyFallback, restoreElement]);

  // Setup MutationObserver for DOM changes
  useEffect(() => {
    if (loading) return;

    const processAllElements = () => {
      document.querySelectorAll('[data-feature]').forEach(processElement);
    };

    processAllElements();

    observerRef.current = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            const element = node as Element;
            if (element.hasAttribute('data-feature')) {
              processElement(element);
            }
            element.querySelectorAll?.('[data-feature]').forEach(processElement);
          }
        });

        if (mutation.type === 'attributes' && mutation.target.nodeType === 1) {
          const element = mutation.target as Element;
          if (element.hasAttribute('data-feature')) {
            processedElements.current.delete(element);
            processElement(element);
          }
        }
      });
    });

    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-feature', 'data-fallback'],
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [loading, processElement]);

  // Expose global functions
  useEffect(() => {

    (window as Window & { useCelo?: Record<string, unknown> }).useCelo = {
      ...((window as Window & { useCelo?: Record<string, unknown> }).useCelo || {}),
      showUpgrade: (featureName: string) => {
        onUpgradeClick?.(featureName);
      },
    };
  }, [onUpgradeClick]);

  const contextValue: FeatureGatingContextType = {};

  return (
    <FeatureGatingContext.Provider value={contextValue}>
      {children}
    </FeatureGatingContext.Provider>
  );
}

export function useFeatureGating() {
  const context = useContext(FeatureGatingContext);
  if (!context) {
    throw new Error('useFeatureGating must be used within FeatureGatingProvider');
  }
  return context;
}